import {
  forwardRef,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type HTMLAttributes,
} from "react"
import { useAutoCollapse } from "@/hooks/use-auto-collapse"
import {
  usePipDrag,
  type PipColumns,
  type PipHorizontal,
  type PipPosition,
} from "@/hooks/use-pip-drag"
import type { VoiceConnectionStatus } from "@/types/voice-chat-rtc"
import { cn } from "cn"
import { AnimatePresence, motion, type MotionProps } from "motion/react"

import {
  BOTTOM_INSET_PROPERTY,
  bottomInsetRef,
  publishedBottomInset,
} from "@/lib/bottom-edge/writer"
import { nowMillis } from "@/lib/date-utils"
import { selectIsXS } from "@/lib/storage/app-slice"
import { hangUpVoiceMode } from "@/lib/storage/chat-thunks"
import { useAppDispatch, useAppSelector } from "@/lib/storage/hooks"
import { selectChatName } from "@/lib/storage/user-slice"
import { restoreVoiceOverlay } from "@/lib/storage/voice-chat-coverage"
import {
  selectVisibleVoiceToolCalls,
  selectVoiceConnectionStatus,
  selectVoiceMuted,
  selectVoicePausable,
  selectVoicePaused,
  selectVoicePipPosition,
  voiceChatActions,
} from "@/lib/storage/voice-chat-slice"
import { publishDockOrbRect, releaseDockOrbRect } from "@/lib/voice-orb-handoff"
import IconCross from "@/components/icons/cross"
import type { IconComponent } from "@/components/icons/icon"
import IconMicrophoneMuted from "@/components/icons/microphone-muted"
import IconPause from "@/components/icons/pause"
import IconPlayFill from "@/components/icons/play-fill"
import { IconButton } from "@/components/ui/icon-button"
import { Portal } from "@/components/ui/portal"
import { VoiceChatOrb } from "@/components/voice-chat-orb"

// The minimized presentation of a live voice session: a glanceable readout plus
// the controls that matter while the call is docked — pause, mute, and end.
// Tapping the body (chip + labels) returns to the full overlay; the controls
// are separate targets so they don't.
//
// Deliberately props-driven (no Redux) so every state it can be in is reachable
// without a live session — VoiceMiniPlayer at the bottom of this file wires it
// to the store. The orb is the one exception: it reads the session's own
// playback metering, and with no session those reads are inert, so it rests.
//
// Pause here is the overlay's semantics at player scale; why the mechanism is
// what it is lives where it is implemented (VoiceChatRTC.setPaused). Speaking
// is shown by the orb's halo alone — a second wash of the same gradient along
// the card's edge was built and removed (2026-08-04), because two brand
// gradients fight on a 64px player.

// The pointer plumbing the drag hook hands down (see use-pip-drag.ts). Listed
// rather than spread from HTMLAttributes so the card's surface stays a
// deliberate list — every one of these is optional, and the card renders
// perfectly well with none of them.
//
// Only the press: a gesture starts on the card and is then followed on the
// window, so there is no move or release handler to hang here.
type VoiceMiniPlayerCardDragProps = Pick<
  HTMLAttributes<HTMLDivElement>,
  "onPointerDown" | "onKeyDown" | "onClickCapture"
>

interface VoiceMiniPlayerCardProps extends VoiceMiniPlayerCardDragProps {
  // The origin chat's name. Undefined for a session started from home before the
  // chat is named — the player falls back to "Voice mode".
  chatName?: string
  // Drives the orb's connecting / speaking / error treatment. Only the resting
  // and error looks are reachable without a live session — the halo needs real
  // playback metering behind it.
  status?: VoiceConnectionStatus
  muted?: boolean
  onMuteToggle?: () => void
  paused?: boolean
  // Whether the pause action is currently available (selectVoicePausable):
  // a substantial turn is being spoken. Status alone flickered the button
  // through tool chains (MIN-13236).
  pausable?: boolean
  onPauseToggle?: () => void
  // Return to the full voice overlay (dismiss the covering sheet).
  onReturn?: () => void
  onClose?: () => void
  // Collapsed, the card is a 64px circle holding nothing but the orb. It gets
  // there on a timer rather than by a control — see useAutoCollapse.
  collapsed?: boolean
  onExpand?: () => void
  // Take keyboard focus on mount. Set when the card is arriving from a
  // collapse, which takes the focused control off the screen with the overlay —
  // see DockedVoiceMiniPlayer.
  takeFocus?: boolean
  // A pending tool call's display name; set, it takes over the description
  // line as shimmer text. Invisible while collapsed — the whole text block
  // fades out there, and that is the design: the circle stays quiet.
  toolLabel?: string
  className?: string
}

// The player's controls at player scale. Nothing here but the size and the
// glyph metric: the resting/filled pair and the disabled treatment come from
// the shared button, so these can no longer drift out of step with the
// overlay's controls the way a hand-rolled copy did.
function MiniPlayerButton({
  icon,
  label,
  active,
  disabled,
  onClick,
}: {
  icon: IconComponent
  label: string
  active?: boolean
  // Collapsed, these are clipped out of the circle. `disabled` is what takes
  // them out of the tab order and tells a screen reader they are unavailable —
  // aria-hidden on a focusable control would leave it reachable but unnamed.
  disabled?: boolean
  onClick?: () => void
}) {
  return (
    <IconButton
      size="sm"
      // The overlay's own pair: `subtle` at rest, the filled primary for the
      // one state worth shouting about.
      variant={active ? "primary" : "subtle"}
      // Held at half the button against IconButton's 1.5em default, which
      // would put a 19.5px glyph inside a 32px circle.
      icon={{ icon, className: "text-md" }}
      label={label}
      disabled={disabled}
      onClick={onClick}
    />
  )
}

// The chat quips' swap transition (message-thinking.tsx's
// DELAY_TEXT_ANIMATION, exit-then-enter under AnimatePresence mode="wait"),
// copied rather than imported so this player's motion can be retuned without
// touching the chat indicator's.
const LABEL_SWAP_ANIMATION: MotionProps = {
  variants: {
    visible: { opacity: 1, y: 0 },
    hidden: { opacity: 0, y: 4 },
    exit: { opacity: 0, y: -4 },
  },
  transition: { duration: 0.2 },
  initial: "hidden",
  animate: "visible",
  exit: "exit",
}

// The indicator's floor. Most voice tool calls take long enough that this
// never engages; the ones that don't (a warm cache hit, a trivial lookup)
// would otherwise flash the label for a few frames — read as a glitch, not as
// "it did something quickly". It also buys every label swap room for the full
// 400ms exit-then-enter crossfade before the next one can start.
const TOOL_INDICATOR_MIN_VISIBLE_MS = 1000

// Latches `label` so that once it appears it stays at least `ms` — the tail
// end holds the LAST label after the store has dropped it. The clock runs
// from the indicator's first appearance, not the current label's: back-to-back
// calls keep it up continuously, so a second call finishing fast doesn't earn
// a second dwell on a player that has already been visible past the floor.
function useMinimumVisible(
  label: string | undefined,
  ms: number
): string | undefined {
  // Deliberately a latched copy of the prop (not the mirror-a-prop smell): the
  // whole point is to disagree with the prop for up to `ms` after it clears.
  // Kept current by render-time adjustment rather than an effect, so the
  // returned value never lags the prop by a frame.
  const [tail, setTail] = useState(label)
  if (label && label !== tail) {
    setTail(label)
  }

  // When the current appearance began; 0 = not showing. A ref because only
  // the hide timer reads it, and writing it must not re-render.
  const shownAtRef = useRef(0)

  useEffect(() => {
    if (label) {
      if (!shownAtRef.current) shownAtRef.current = nowMillis()
      return
    }
    if (!shownAtRef.current) return
    // Always through a timeout, even when the floor has already passed —
    // remaining just clamps to 0. If the label comes back before it fires,
    // the cleanup cancels the hide and the appearance simply continues.
    const remaining = shownAtRef.current + ms - nowMillis()
    const timer = setTimeout(
      () => {
        shownAtRef.current = 0
        setTail(undefined)
      },
      Math.max(0, remaining)
    )
    return () => clearTimeout(timer)
  }, [label, ms])

  return label ?? tail
}

// The chat thinking indicator's treatment (message-thinking.tsx), spelled out
// here rather than shared: this one needs w-fit and a dark-mode rest colour
// neither other site wants.
//
// w-fit is load-bearing. The `shimmer` utility sizes its gradient off the
// ELEMENT, so the glint only clears the text if the box IS the text — free
// everywhere else, but this line stays block-level to hold the description
// slot's height and to truncate. Stretched, the box ran ~4× the glyphs and the
// sweep lit instantly, peaked early, then swept empty space: a dead beat.
const SHIMMER_CLASSES =
  "w-fit max-w-full shimmer shimmer-duration-1500 shimmer-spread-32 dark:text-muted"

export const VoiceMiniPlayerCard = forwardRef<
  HTMLDivElement,
  VoiceMiniPlayerCardProps
>(function VoiceMiniPlayerCard(
  {
    chatName,
    status = "listening",
    muted = false,
    onMuteToggle,
    paused = false,
    pausable = false,
    onPauseToggle,
    onReturn,
    onClose,
    collapsed = false,
    onExpand,
    takeFocus = false,
    toolLabel,
    className,
    ...dragProps
  },
  ref
) {
  // Published rather than held: the overlay flies its own orb onto this one and
  // back off it, and after a collapse the overlay is the only one of the two
  // still mounted.
  const chipRef = useRef<HTMLSpanElement>(null)

  // Collapsing takes the focused control off the screen with the overlay, and
  // focus falls to <body>: a keyboard user loses their place and is told
  // nothing about where the call went. The body button is the way back, which
  // is the thing they were last doing.
  const bodyRef = useRef<HTMLButtonElement>(null)
  useEffect(() => {
    if (takeFocus) bodyRef.current?.focus()
  }, [takeFocus])

  // Deliberately no dependency list. The card does not position itself — it is
  // moved by the classes on its positioner — so there is nothing it could name
  // that would catch a drag to another slot, and a rect left over from where
  // the dock mounted sends the return flight to the wrong corner entirely.
  // Re-reading per commit is one rect on a card that renders a handful of times
  // a session.
  useLayoutEffect(() => {
    const chip = chipRef.current
    if (chip) publishDockOrbRect(chip.getBoundingClientRect())
  })

  // The things that move the chip without re-rendering this card.
  useLayoutEffect(() => {
    const publish = () => {
      const chip = chipRef.current
      if (chip) publishDockOrbRect(chip.getBoundingClientRect())
    }
    // The positioner sliding the card down with the bottom nav it clears. Its
    // transitionend goes UP, so the card's own handler below never sees it,
    // and the card does not re-render for a nav that hides on scroll — the
    // rect would otherwise stay a nav's height above where the orb now is.
    const positioner = chipRef.current?.closest(
      "[data-slot=voice-mini-player]"
    )?.parentElement
    const onPositionerMove = (event: TransitionEvent) => {
      if (event.target === positioner && event.propertyName === "bottom") {
        publish()
      }
    }
    positioner?.addEventListener("transitionend", onPositionerMove)
    window.addEventListener("resize", publish)
    return () => {
      positioner?.removeEventListener("transitionend", onPositionerMove)
      window.removeEventListener("resize", publish)
      // Releasing does not erase the rect — the return journey flies out of it,
      // and by then this card is gone. It retires the claim that the rect is
      // where a dock IS, so the next collapse waits for the card that is
      // arriving instead of aiming at the one that left.
      releaseDockOrbRect()
    }
  }, [])

  return (
    <div
      ref={ref}
      // Addressable from outside the React tree, which this needs more than
      // most: the player portals to <body>, so it is nowhere near its owner in
      // the DOM.
      data-slot="voice-mini-player"
      // The fold animates the card's width, which moves the chip with it
      // whenever the card is pinned by its right edge. Own transitions only —
      // the label block and the control row fade on their own clocks.
      onTransitionEnd={(event) => {
        if (event.target !== event.currentTarget) return
        const chip = chipRef.current
        if (chip) publishDockOrbRect(chip.getBoundingClientRect())
      }}
      {...dragProps}
      className={cn(
        // Same glass as the mobile nav and composer it floats beside;
        // glass-bar's hairline ring is the border, hence no separate `border`.
        //
        // py-3 sizes the card off the orb (40px, 48 from `sm`) rather than the
        // shorter text block, landing it at 64px and 72. overflow-hidden then
        // guarantees the halo cannot draw a flat chord across a straight edge —
        // it peaks ~5.6px inside them, ~4.9px on the wider card, so shrinking
        // the padding spends that clearance.
        //
        // Capped from `sm` only. Below it the expanded card has one column and
        // stays pinned to its side, so on a phone wider than the cap the whole
        // surplus landed on the far side; uncapped it fills the row like the
        // nav pill.
        "flex items-center gap-3 overflow-hidden py-3 glass-bar sm:max-w-md",
        // What makes the drag work on iOS: WebKit does not apply an ancestor's
        // `touch-action: none` to a touch that starts inside SVG content. Every
        // glyph here is an <svg> and folded the orb IS the card, so a thumb
        // landed on SVG nearly every time and iOS read the gesture as a page
        // pan. Transparent to hit-testing, the touch lands on the nearest HTML
        // box, where touch-action is honoured; clicks are unaffected, having
        // always bubbled to the buttons around them.
        //
        // Scoped to the glyphs, NOT the chip: folded, the controls squeeze the
        // body button to nothing and the chip is the expand tap's only target.
        "[&_svg]:pointer-events-none",
        // The SAME card collapsed, not a second component: the orb grows its
        // halo in over 300ms off live metering, so a swap would make it visibly
        // reconnect on every fold. Only the width changes: a capsule either way.
        "rounded-full px-3",
        collapsed ? "w-16 sm:w-18" : "w-full",
        // Width is a layout property, which this codebase otherwise avoids
        // animating. It is safe here because the card is fixed, portaled and
        // clipped with three children — nothing outside it can reflow. Sits in
        // the range the drag's release spring settles in (see
        // springSnapKeyframes in use-pip-drag.ts) so a collapse and a throw
        // feel like one family.
        "transition-[width] duration-300 ease-drawer",
        className
      )}
    >
      {/* Body — tap to return to the full overlay. A button so it's keyboard-
        reachable; the chip + labels live inside it (the mute/end controls are
        siblings, never nested, so they stay independently tappable).
        Collapsed, the circle is all body, and the tap expands rather than
        returning: getting to voice from here is two deliberate taps, not one
        that could be either. */}
      <button
        ref={bodyRef}
        type="button"
        aria-label={collapsed ? "Expand voice player" : "Return to voice chat"}
        onClick={collapsed ? onExpand : onReturn}
        className="flex min-w-0 flex-1 items-center gap-3 text-left"
      >
        {/* The same orb as the full overlay, at player scale: the session's
          identity, and what carries connecting, speaking, and error state
          here. Calmer than the overlay's — a wider halo that moves less (see
          the `mini` size in VoiceChatOrb), because the player sits over a sheet
          the user is reading. */}
        <span ref={chipRef} className="flex shrink-0">
          <VoiceChatOrb status={status} size="mini" />
        </span>

        <span
          className={cn(
            "flex min-w-0 flex-col transition-opacity duration-200",
            collapsed && "opacity-0"
          )}
        >
          {chatName ? (
            <span className="no-translate truncate text-sm font-semibold text-foreground sm:text-base">
              {chatName}
            </span>
          ) : (
            <span className="truncate text-sm font-semibold text-foreground sm:text-base">
              Voice mode
            </span>
          )}
          {/* The description line. A running tool call takes it over as
            shimmer text — the overlay's own tool treatment, telling "working"
            from "live" by motion at a size where dots or a spinner would just
            be clutter. It outranks the resting "Voice mode"; one day the
            session elapsed time (e.g. "0:28") will sit here too, once a
            session start timestamp is wired up.

            The slot holds its exact height for the whole time the line has
            something to say, including mode="wait"'s empty beat between one
            label's exit and the next one's enter — otherwise every swap would
            collapse the line and bounce the title. It is dropped entirely when
            the line has nothing at all: an unnamed session shows no resting
            text (its title already says "Voice mode"), and reserving 7px of
            blank under it just hung the title above centre. That condition is
            stable for the session, not per-swap, so the anti-bounce guarantee
            above is untouched.

            Foreground-based shimmer, muted rest — the activity feed's exact
            grammar (message-activity-feed.tsx promotes its latest streaming
            line to foreground inside a text-muted list): the snap from grey
            to black IS the signal that work started. A muted shimmer doesn't
            carry it: the sweep derives from currentColor, so on muted grey at
            this size it is barely visible. */}
          {(toolLabel || chatName) && (
            <span className="block h-(--text-2xs--line-height) sm:h-(--text-xs--line-height)">
              <AnimatePresence mode="wait" initial={false}>
                {toolLabel ? (
                  <motion.span
                    // Keyed on the label so a tool→tool handoff crossfades too,
                    // not only the Voice mode boundaries.
                    key={toolLabel}
                    {...LABEL_SWAP_ANIMATION}
                    className={cn(
                      "block truncate text-2xs sm:text-xs",
                      SHIMMER_CLASSES
                    )}
                  >
                    {toolLabel}
                  </motion.span>
                ) : (
                  chatName && (
                    <motion.span
                      key="voice-mode"
                      {...LABEL_SWAP_ANIMATION}
                      className="block truncate text-2xs text-muted sm:text-xs"
                    >
                      Voice mode
                    </motion.span>
                  )
                )}
              </AnimatePresence>
            </span>
          )}
        </span>
      </button>

      {/* Fades as the card narrows, rather than being hard-clipped by the
        closing edge halfway through. Still mounted while collapsed, so the row
        never reflows — `disabled` is what actually makes it unreachable. */}
      <div
        className={cn(
          "flex shrink-0 items-center gap-1 transition-opacity duration-200",
          collapsed && "pointer-events-none opacity-0"
        )}
      >
        {/* Pause/resume the assistant's speech — the mic is left alone, so a
          paused customer can just say what they wanted to say, which ends the
          pause. Glyph is the ACTION, not the state. At rest an outline
          pause on the quiet surface; paused flips to the filled play on the
          filled "active" (black) surface — the overlay's exact treatment at
          player scale. Only live while a substantial turn is being spoken
          (`pausable`); resume always stays live. */}
        <MiniPlayerButton
          icon={paused ? IconPlayFill : IconPause}
          label={paused ? "Resume" : "Pause"}
          active={paused}
          disabled={collapsed || (!paused && !pausable)}
          onClick={onPauseToggle}
        />
        {/* One glyph for both states: the slashed mic, with the filled
          background carrying the muted state. Matches the full overlay's
          control. Live through a pause: the uplink is what a pause leaves
          alone, so mute stays the way to hold the room out of the session. */}
        <MiniPlayerButton
          icon={IconMicrophoneMuted}
          label={muted ? "Unmute microphone" : "Mute microphone"}
          active={muted}
          disabled={collapsed}
          onClick={onMuteToggle}
        />
        <MiniPlayerButton
          icon={IconCross}
          label="End voice chat"
          disabled={collapsed}
          onClick={onClose}
        />
      </div>
    </div>
  )
})

// Which edge of the card holds still while the fold animates its width. Always
// the pinned side, never the centre: re-centring on expand snapped the card to
// the middle of the screen at its collapsed width the instant the class
// changed, so an orb pinned left jumped across before it grew.
const JUSTIFY: Record<PipHorizontal, string> = {
  left: "justify-start",
  center: "justify-center",
  right: "justify-end",
}

// The card in its docked position — where the player sits relative to whatever
// occupies the bottom edge, which is the whole of its relationship with the
// sheet covering it.
//
// Separate from the store wiring below so the positioning can be exercised
// against a given position and viewport without a live session standing behind
// it — this is the part with real behaviour in it, and the part a test has to
// be able to reach directly rather than through a copy.
export function DockedVoiceMiniPlayer({
  position,
  onPositionChange,
  foldsAway = false,
  fillsRow = false,
  onMuteToggle,
  onPauseToggle,
  toolLabel,
  entry = "coverage",
  ...props
}: Omit<
  VoiceMiniPlayerCardProps,
  "className" | "collapsed" | "onExpand" | keyof VoiceMiniPlayerCardDragProps
> & {
  position: PipPosition
  onPositionChange: (next: PipPosition) => void
  // Folds itself away to a circle a few seconds in, so it stops covering what
  // is behind it.
  foldsAway?: boolean
  // The expanded card spans its track (a phone), so it can only move up and
  // down, and its folded circle docks left or right.
  fillsRow?: boolean
  entry?: VoiceMiniPlayerEntry
}) {
  // Folds itself away five seconds in.
  const { collapsed, expand, poke, setHeld } = useAutoCollapse({
    enabled: foldsAway,
  })

  // Where the card fills the row it can only move up and down, and its folded
  // 64px circle docks left or right. With room for the card, card and circle
  // alike rest left, centered, or right.
  let columns: PipColumns = 3
  if (fillsRow) columns = collapsed ? 2 : 1

  // The floor is applied here rather than in the card so the card stays a
  // dumb readout — every state it can show remains directly reachable in a
  // test or story by just passing the prop.
  const visibleToolLabel = useMinimumVisible(
    toolLabel,
    TOOL_INDICATOR_MIN_VISIBLE_MS
  )

  const { ref, ...dragHandlers } = usePipDrag<HTMLDivElement>({
    position,
    onPositionChange,
    columns,
    // A move from the keyboard is over the moment it happens, so it just earns
    // a fresh countdown.
    onInteraction: poke,
    // A gesture isn't: the countdown stands down for as long as a finger is on
    // the player, and the five seconds start again from the release.
    onHoldChange: setHeld,
  })

  // The two anchors are mutually exclusive: whichever one isn't in play resolves
  // to `auto`, so a slot swap is a snap between two different properties rather
  // than a transition. That is deliberate — the travel between slots is the
  // card's FLIP, and a positioner animating underneath it would double it.
  // Both sides transition because both can move underneath a resting card: the
  // top slot when its clearance changes, the bottom slot when the furniture
  // under it moves (the nav hiding on scroll, a booking bar arriving). Neither animates on a slot SWAP — the property that isn't in play
  // is `auto`, and auto↔length is a discrete jump, not a transition.
  const anchor =
    position.vertical === "top"
      ? "top-(--player-top) transition-[top] duration-drawer ease-drawer"
      : "bottom-(--player-bottom) transition-[bottom] duration-drawer ease-drawer"

  // On a phone the folded circle has no center column; it docks left, where
  // the orb sits inside the expanded card (see DEFAULT_PIP_POSITION).
  const justify =
    fillsRow && collapsed && position.horizontal === "center"
      ? JUSTIFY.left
      : JUSTIFY[position.horizontal]

  const insetAtMount: Record<string, string> = {}
  const inset = publishedBottomInset()
  if (inset) insetAtMount[BOTTOM_INSET_PROPERTY] = inset

  return (
    // Portaled to <body>, outside the app tree, so a modal sheet's outside-press
    // dismissal cannot reach the card's own controls — mute especially — while
    // the user keeps reading the sheet.
    <Portal
      // Reads --bottom-inset, like the audio guide player and toasts, so the
      // card clears whatever bottom furniture is painted: the nav, a composer,
      // a sheet's booking bar. Also stamped at creation: the card publishes its
      // orb's rect from a layout effect, and a child's layout effects run before
      // a parent's ref attaches, so without it the first rect — the one the
      // collapse flies to — is taken with the inset unset.
      render={<div ref={bottomInsetRef} style={insetAtMount} />}
      className={cn(
        // The audio guide player's frame: 1rem in from each side on a phone,
        // the area right of the nav from `sm`, and --player-gap above the
        // bottom furniture. On a phone that also keeps the card 0.5rem inside a
        // sheet's drawer, which is inset 0.5rem from the screen.
        "pointer-events-none fixed inset-x-0 z-40 flex px-4 sm:left-(--nav-width)",
        "[--player-bottom:calc(max(var(--bottom-inset,0px),env(safe-area-inset-bottom,0px))+var(--player-gap,1rem))]",
        // The top slot's one refusal: a covering sheet's header, which on a
        // phone owns the whole strip (Close / share / save / add-to-trip).
        // Sitting over that covers the way out, not content. A desktop has no
        // such takeover, so from `sm` the clearance is zero and the two slots
        // mirror each other.
        "[--player-clear:var(--header-height,3rem)] sm:[--player-clear:0px]",
        "[--player-top:calc(env(safe-area-inset-top,0px)+var(--player-clear,0px)+var(--player-gap,1rem))]",
        // Matches the audio guide player, so the two docked modality surfaces
        // sit at the same inset. Every var() here carries a fallback: one that
        // resolves to nothing invalidates the whole declaration, and no
        // fallback on the reader can catch that.
        "[--player-gap:1rem] sm:[--player-gap:1.5rem]",
        anchor,
        justify
      )}
    >
      <VoiceMiniPlayerCard
        ref={ref}
        // Only on a hand-off. Arriving under a sheet, focus belongs to the
        // sheet that just opened, and taking it would be the same defect
        // pointing the other way.
        takeFocus={entry === "handoff"}
        toolLabel={visibleToolLabel}
        className={cn(
          "pointer-events-auto motion-safe:animate-in motion-safe:ease-drawer motion-safe:fade-in",
          // fill-mode-backwards holds the from-frame (opacity 0) through the
          // delay — without it the player would sit fully visible for that
          // delay and then restart from transparent.
          "motion-safe:fill-mode-backwards",
          ENTRY_TIMING[entry],
          // touch-none is what lets a drag start here at all: without it the
          // browser claims the gesture for the sheet's scroller before the first
          // pointermove lands. Scoped to the card, so touching the sheet
          // anywhere else still scrolls it normally.
          "touch-none select-none [-webkit-touch-callout:none]"
        )}
        collapsed={collapsed}
        onExpand={expand}
        // Muting is worth staying open for: a countdown that ran out mid-tap
        // would fold the player away just as the user reached past it for End.
        onMuteToggle={
          onMuteToggle &&
          (() => {
            poke()
            onMuteToggle()
          })
        }
        // Same again for pause — and a paused player folding itself away would
        // hide the very control that proves the session is only suspended.
        onPauseToggle={
          onPauseToggle &&
          (() => {
            poke()
            onPauseToggle()
          })
        }
        {...dragHandlers}
        {...props}
      />
    </Portal>
  )
}

// Why the dock is appearing, which is the only thing that decides how it does.
//
// Over a covering sheet it waits, so the fade arrives over a settled scene
// instead of racing the sheet's entrance. From a collapse there is nothing to
// wait for and the wait is the defect, so it comes up inside the orb's flight.
//
// That entry is pinned to the orb's exchange window exactly (HANDOFF_MS -
// ORB_EXCHANGE_MS, then ORB_EXCHANGE_MS), and the alignment is load-bearing.
// This card paints ABOVE the overlay whatever the z-indexes say — it portals to
// <body> while the overlay sits in <main>, which is `isolation: isolate` — and
// neither can move (the dock must outrank the sheet layer, the overlay must sit
// under it). So the only frames it may paint in are the ones where the
// traveller is already at the chip's centre at the chip's size, and being on
// top of it cannot be seen. Appearing any earlier puts a materializing card in
// front of an orb still in mid-flight.
export type VoiceMiniPlayerEntry = "coverage" | "handoff"

const ENTRY_TIMING: Record<VoiceMiniPlayerEntry, string> = {
  coverage: "motion-safe:delay-150 motion-safe:duration-drawer",
  handoff: "motion-safe:delay-[220ms] motion-safe:duration-[80ms]",
}

// Store-wired. Navigating away from the origin chat ends the call (the voice
// middleware closes on the chat-id change), so there is no off-origin
// mini-player: whenever this renders, the current chat IS the voice chat.
export function VoiceMiniPlayer({
  entry = "coverage",
}: {
  entry?: VoiceMiniPlayerEntry
}) {
  const dispatch = useAppDispatch()
  const muted = useAppSelector(selectVoiceMuted)
  const paused = useAppSelector(selectVoicePaused)
  const pausable = useAppSelector(selectVoicePausable)
  const status = useAppSelector(selectVoiceConnectionStatus)
  // The current chat is the origin chat here, so its name is the session's —
  // read live rather than snapshotting, so a rename stays current.
  const chatName = useAppSelector(selectChatName)
  // Whether the card fills its row. `xs` is exactly the range Tailwind's `sm:`
  // variant flips at (see MEDIA_QUERIES.xsMax), so this gate and the dock's
  // breakpoint classes always agree about which layout is on screen — the card
  // that is capped at `sm` is the same card that gains its three columns here.
  const isXS = useAppSelector(selectIsXS)
  // Slice state, not component state: expanding the call unmounts this player,
  // so a corner held here went back to the default every time the user looked
  // at the overlay and came back. The slice's copy lives exactly as long as the
  // call, and is never persisted — a corner is a shove out of the way of what
  // was being read, and that expires with the call. One corner for every
  // viewport, so a resize leaves the card where the user put it.
  const position = useAppSelector(selectVoicePipPosition)
  // Reduced to a string INSIDE the selector, on voice-chat.tsx's precedent:
  // the visible-calls array is rebuilt on every live-stream tick (caption
  // deltas included), so subscribing to it directly would re-render the whole
  // dock at caption rate. The newest pending call wins — that's the one the
  // assistant is actually waiting on.
  const toolLabel = useAppSelector((state) => {
    const pending = selectVisibleVoiceToolCalls(state).filter(
      (call) => call.status === "pending"
    )
    return pending[pending.length - 1]?.progressLabel
  })

  return (
    <DockedVoiceMiniPlayer
      chatName={chatName ?? undefined}
      status={status}
      muted={muted}
      paused={paused}
      pausable={pausable}
      position={position}
      onPositionChange={(next) =>
        dispatch(voiceChatActions.voicePipMoved(next))
      }
      foldsAway
      fillsRow={isXS}
      entry={entry}
      toolLabel={toolLabel}
      onMuteToggle={() => dispatch(voiceChatActions.mutedToggled())}
      onPauseToggle={() => dispatch(voiceChatActions.pausedToggled())}
      // Nothing between the user and the full overlay but the state that
      // replaced it — a covering sheet, a collapse, or both. Navigation
      // wouldn't move either, so clear them.
      onReturn={() => void dispatch(restoreVoiceOverlay())}
      onClose={() => void dispatch(hangUpVoiceMode())}
    />
  )
}
